import pdfMake from 'pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { useStore } from "vuex";
import figureFormatter from "@/services/utils/figureFormatter";
import useDate from '@/services/utils/day';
import { formatMonthRangeLabel, groupBy } from '@/services/utils/global';
import { sortBy, sumBy } from 'lodash';
import { isVisible, getActiveSetting, getPageMargins, resolveFontSize } from '@/services/utils/pdf/pdfCustomizationHelper';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

pdfMake.fonts = {
    SulaimanLipi: {
        normal: 'https://fonts.cdnfonts.com/s/14639/solaimanlipi.woff',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf'
    }
}
const pdfPrinter = () => {
    const store = useStore();
    const {commaFormat, formatNameWithSerial} = figureFormatter();
    const { formatDate } = useDate();

    const userName = store.state.user.user.name;
    const defaultPageMargins = [10, 100, 10, 80];
    const defaultFontSize = 8;
    const pageWidth = 595.28;
    const defaultFooterNote = 'N.B: This is a system generated documents and requires no manual signature.';

    const exportToPDF = (company, incentivePaymentData, isReport = false, isBillCollection = false, date = {}, customizationFields = {}) => {
        const pageMargins = getPageMargins(customizationFields, defaultPageMargins);

        var doc = {
            pageSize: 'A4',
            pageMargins: pageMargins,
            header: getHeader(company, customizationFields, pageMargins),
            footer: getFooter(userName, customizationFields, pageMargins),
            content: getContent(incentivePaymentData, isReport, isBillCollection, date, customizationFields),

            styles: {
                header: {
                    fontSize: 24,
                }
            },

            defaultStyle: {
                color: 'black',
                fontSize: resolveFontSize(customizationFields, defaultFontSize),
                font: 'SulaimanLipi'
            },

            info: {
                title: getTitle(isReport, isBillCollection),
            }
        }

        pushContentBody(doc, incentivePaymentData, isReport, isBillCollection, customizationFields)

        const pdfDocGenerator = pdfMake.createPdf(doc);
        pdfDocGenerator.open();
    }

    function getTitle(isReport, isBillCollection) {
        if (isReport && isBillCollection) return "Bill Collection Report";
        if (isReport && !isBillCollection) return "Incentive Statement";
        if (!isReport && isBillCollection) return "Draft Bill Collection";
        return "Draft Incentive Payment";
    }

    const validValue = (value) => {
        if (! value) return '';
        if (value === 'null') return '';
        if (value === 'undefined') return '';
        return value;
    }

    const getHeader = (company, customizationFields, pageMargins) => {
        const coverPhoto = getActiveSetting(customizationFields, 'extra_settings_cover_photo_upload');

        if (coverPhoto && coverPhoto.value64) {
            return {
                columns: [
                    {
                        image: coverPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[1] || defaultPageMargins[1],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 25, 25, 25, 10 ],
            columns: [
                isVisible(customizationFields, 'heading_left_logo') ? {
                    alignment: 'left',
                    image: company.logo64,
                    maxHeight: 60
                } : null,

                {
                    alignment: 'right',
                    stack: getCompanyLines(company, customizationFields)
                }
            ].filter(column => !! column)
        }
    }

    const getCompanyLines = (company, customizationFields) => {
        const address = validValue(company.address);
        const phone = validValue(company.phone);
        const email = validValue(company.email);
        const vatNo = validValue(company.vat_no);

        return [
            { key: 'heading_right_company_name', text: validValue(company.name) },
            { key: 'heading_right_company_address', text: address ? 'Address: ' + address : '' },
            { key: 'heading_right_company_phone', text: phone ? 'Phone: ' + phone : '' },
            { key: 'heading_right_company_email', text: email ? 'E-mail: ' + email : '' },
            { key: 'heading_right_vat_reg_number', text: vatNo ? 'VAT Reg No: ' + vatNo : '' },
        ].filter(line => isVisible(customizationFields, line.key)).map(line => line.text)
    }

    const getFooterNote = (customizationFields) => {
        const setting = getActiveSetting(customizationFields, 'extra_settings_footer_note');
        if (! setting) return defaultFooterNote;
        return setting.value || defaultFooterNote;
    }

    const getFooter = (userName, customizationFields, pageMargins) => {
        const footerPhoto = getActiveSetting(customizationFields, 'extra_settings_footer_photo_upload');

        if (footerPhoto && footerPhoto.value64) {
            return {
                columns: [
                    {
                        image: footerPhoto.value64,
                        width: pageWidth,
                        height: pageMargins[3] || defaultPageMargins[3],
                        alignment: 'center'
                    }
                ]
            }
        }

        return {
            margin: [ 25, 0 ],
            columns: [
                isVisible(customizationFields, 'footer_note') ? {
                    width: '*',
                    alignment: 'left',
                    text: getFooterNote(customizationFields),
                    margin: [5, 53, 0, 0],
                    fontSize: 8
                } : { width: '*', text: '' },

                isVisible(customizationFields, 'footer_printed_by') ? {
                    width: '20%',
                    alignment: 'right',
                    fontSize: 8,
                    margin: [0, 33, 0, 0],
                    stack: [
                        {
                            text: 'Printed by',
                            bold: true
                        },
                        {
                            text: userName
                        },
                        {
                            text: formatDate(new Date())
                        }
                    ]
                } : null
            ].filter(column => !! column)
        }
    }

    const getContent = (incentivePaymentData, isReport, isBillCollection, date, customizationFields) => {
        return [
            ...getTitleNode(isReport, isBillCollection, date, customizationFields),

            ...getReferrerInfo(incentivePaymentData, isReport, isBillCollection, customizationFields),

            ...getMainTable(isReport, isBillCollection),
        ]
    }

    const getMainTable = (isReport, isBillCollection) => {
        if (! isBillCollection) return [];

        return [getTableNode(customWidth(isReport, isBillCollection), [])];
    }

    const getTableNode = (widths, body) => {
        return {
            style: 'tableExample',
            margin: [0, 10, 0, 0],
            table: {
                body: body,
                widths: widths
            },
            layout: {
                fillColor: function (rowIndex, node, columnIndex) {
                    return (rowIndex === 0) ? '#f3f2f7' : null;
                },
                hLineWidth: function (i, node) {
                    return 0.5;
                },
                vLineWidth: function (i, node) {
                    return 0.5;
                }
            }
        };
    }

    const getTableItems = (incentivePaymentData) => {
        return Array.isArray(incentivePaymentData.data.data)
            ? incentivePaymentData.data.data
            : Object.values(incentivePaymentData.data.data);
    }

    const pushContentBody = (doc, incentivePaymentData, isReport, isBillCollection, customizationFields) => {
        if (isBillCollection) {
            pushIntoTable(doc, incentivePaymentData, isReport, isBillCollection);
            return;
        }

        if (isReport) {
            doc.content.push(...getReferrerTables(incentivePaymentData, customizationFields));
            return;
        }

        doc.content.push(...getReportTable(getReportColumns(customizationFields), getTableItems(incentivePaymentData), customizationFields));
    }

    const getReferrerTables = (incentivePaymentData, customizationFields) => {
        const columns = getReportColumns(customizationFields);

        return getReferrerGroups(incentivePaymentData).flatMap(group => [
            ...getReferrerLine(group[0], customizationFields),
            ...getReportTable(columns, group, customizationFields),
            ...getReferrerIncentiveBox(group, customizationFields)
        ]);
    }

    const getReferrerGroups = (incentivePaymentData) => {
        return sortBy(
            Object.values(groupBy(getTableItems(incentivePaymentData), 'referrer_id')),
            group => group[0].referrer_name
        );
    }

    const getReferrerLine = (row, customizationFields) => {
        if (! isVisible(customizationFields, 'body_top_left_referrer_name')) return [];

        return [
            {
                text: `Referrer: ${formatNameWithSerial(row.referrer_name, row.referrer_serial_no)}`,
                bold: true,
                fontSize: 9,
                margin: [0, 10, 0, 0]
            }
        ];
    }

    const getReportColumns = (customizationFields) => {
        return [
            { key: 'body_table_invoice_date', header: 'Invoice Date', cell: row => row.date },
            { key: 'body_table_invoice_no', header: 'Invoice No', cell: row => row.bill_number },
            { key: 'body_table_patient_name', header: 'Patient Name (ID)', cell: row => row.patient_name + '(' + row.patient_serial_no + ')' },
            { key: 'body_table_total_bill', header: 'Total Bill', field: 'subtotal_amount' },
            { key: 'body_table_total_discount', header: 'Total Discount', field: 'total_discount' },
            { key: 'body_table_net_bill', header: 'Net Bill', field: 'total_amount' },
            { key: 'body_table_total_incentive', header: 'Total Incentive', field: 'incentive_amount' },
            { key: 'body_table_referrer_discount', header: 'Referrer Discount', field: 'referrer_discount_amount' },
            { key: 'body_table_net_incentive', header: 'Net Incentive', field: 'net_incentive' },
        ].filter(column => isVisible(customizationFields, column.key))
    }

    const toCell = (column, text) => column.field ? { text: text, alignment: 'right' } : { text: text };

    const getCellText = (column, row) => column.field ? commaFormat(row[column.field]) : column.cell(row);

    const getReportTable = (columns, rows, customizationFields) => {
        if (! columns.length) return [];

        return [getTableNode(columns.map(() => '*'), getReportTableBody(columns, rows, customizationFields))];
    }

    const getReportTableBody = (columns, rows, customizationFields) => {
        const header = columns.map(column => toCell(column, column.header));
        const body = rows.flatMap(row => [
            columns.map(column => toCell(column, getCellText(column, row))),
            ...getItemDetailsRow(columns, row, customizationFields)
        ]);
        if (! isVisible(customizationFields, 'body_totals_table_total_row')) return [header, ...body];
        if (! columns.some(column => column.field)) return [header, ...body];
        return [header, ...body, getReportTotalRow(columns, rows)];
    }

    const getItemDetailsRow = (columns, row, customizationFields) => {
        if (! isVisible(customizationFields, 'body_table_item_details')) return [];

        return [[{ text: row.item_details || '', colSpan: columns.length }, ...Array(columns.length - 1).fill({})]];
    }

    const getReportTotalRow = (columns, rows) => {
        const labelSpan = columns.findIndex(column => column.field);
        const totalCells = columns.slice(labelSpan).map(column => ({
            text: commaFormat(sumBy(rows, row => Number(row[column.field]) || 0)),
            alignment: 'right'
        }));
        if (labelSpan === 0) return totalCells;
        return [{ text: 'Total', alignment: 'right', colSpan: labelSpan }, ...Array(labelSpan - 1).fill({}), ...totalCells];
    }

    const getTitleNode = (isReport, isBillCollection, date, customizationFields) => {
        if (! isVisible(customizationFields, 'body_top_left_report_title')) return [];

        return [
            {
                text: getContentTitle(isReport, isBillCollection, date, customizationFields),
                style: 'header',
                alignment: 'center',
                margin: [0, 0, 20, 10],
                bold: true,
                color: 'black',
                fontSize: 12,
                decoration: 'underline'
            }
        ];
    }

    const getContentTitle = (isReport, isBillCollection, date, customizationFields) => {
        const title = getTitle(isReport, isBillCollection);
        if (! isVisible(customizationFields, 'body_top_left_date_range')) return title;
        const monthLabel = formatMonthRangeLabel(date.startDate, date.endDate);
        return monthLabel ? `${title} - ${monthLabel}` : title;
    }

    const getReferrerInfo = (incentivePaymentData, isReport, isBillCollection, customizationFields) => {
        if (isReport || isBillCollection) return [];
        if (! isVisible(customizationFields, 'body_top_left_referrer_name')) return [];

        const tableItems = Object.values(incentivePaymentData.data.data);
        if (tableItems.length < 1) return [];

        return [
            {
                text: `Name: ${formatNameWithSerial(tableItems[0].referrer_name, tableItems[0].referrer_serial_no)}`,
                alignment: 'left',
                margin: [0, 10, 0, 0]
            }
        ];
    }

    const customWidth = (isReport = false, isBillCollection) => {
        if (isBillCollection) {
            return isReport
                ? ['15%', '15%', '15%', '15%', '13%', '13%', '14%']
                : ['17%', '18%', '20%', '15%', '15%', '15%'];
        }

        return isReport
            ? ['10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%', '10%']
            : ['12%', '13%', '15%', '10%', '10%', '10%', '10%', '10%', '10%'];
    }

    const pushIntoTable = (doc, incentivePaymentData, isReport, isBillCollection) => {
        const tableItems = getTableItems(incentivePaymentData);
        const tableHeader = getTableHeader(isReport, isBillCollection);
        const tableBody = doc.content[doc.content.length - 1].table.body;
        tableBody.push(tableHeader);

        tableItems.forEach((item, i) => {
            const rowData = getRowData(item, i, isReport, isBillCollection);
            tableBody.push(rowData);

            const detailsRow = getRowDetails(item.item_details || '', isReport, isBillCollection);
            tableBody.push(detailsRow);
        });

        const getLastRow = getTotal(incentivePaymentData.data.total, isReport, isBillCollection)
        tableBody.push(getLastRow);
    }

    const getRowDetails = (itemDetails, isReport, isBillCollection) => {
        let colSpanValue = isReport ? 10 : 9;
        let emptyCells   = isReport ? 9 : 8;

        if (isBillCollection && isReport) {
            colSpanValue = 7;
            emptyCells   = 6;
        }

        if (isBillCollection && !isReport) {
            colSpanValue = 6;
            emptyCells   = 5;
        }

        return [
            { text: itemDetails, colSpan: colSpanValue },
            ...Array(emptyCells).fill({})
        ];
    };

    const getTableHeader = (isReport, isBillCollection) => {
        let tableHeader = [
            {text: 'Invoice Date'},
            {text: 'Invoice No'},
            {text: 'Patient Name (ID)'},
            {text: 'Total Bill', alignment: 'right'},
            {text: 'Total Discount', alignment: 'right'},
            {text: 'Net Bill', alignment: 'right'},
        ];

        if (isBillCollection && isReport){
            tableHeader.unshift({text: 'Client Name'});
            return tableHeader;
        }

        if (isBillCollection && !isReport){
            return tableHeader;
        }

        tableHeader.push(
            {text: 'Total Incentive', alignment: 'right'},
            {text: 'Referrer Discount', alignment: 'right'},
            {text: 'Net Incentive', alignment: 'right'},
        )

        if (isReport) {
            tableHeader.unshift({text: 'Referrer'});
        }

        return tableHeader;
    }

    const getRowData = (item, i, isReport, isBillCollection) => {
        const tableRow = [
            {text: item.date},
            {text: item.bill_number}
       ];

       if (isBillCollection){
           tableRow.push(
               {text: item.contact_profile_name + '(' + item.contact_profile_serial_no + ')' },
           )
       }

        if (!isBillCollection){
            tableRow.push(
                {text: item.patient_name + '(' + item.patient_serial_no + ')' }
            )
        }

        tableRow.push(
            {text: commaFormat(item.subtotal_amount), alignment: 'right'},
            {text: commaFormat(item.total_discount), alignment: 'right'},
            {text: commaFormat(item.total_amount), alignment: 'right'},
        )

        if (isBillCollection && isReport){
            tableRow.unshift({text: item.client_profile_name + '(' + item.client_serial_no + ')'});
            return tableRow;
        }

        if (isBillCollection && !isReport){
            return tableRow;
        }

        tableRow.push(
            {text: commaFormat(item.incentive_amount), alignment: 'right'},
            {text: commaFormat(item.referrer_discount_amount), alignment: 'right'},
            {text: commaFormat(item.net_incentive), alignment: 'right'},
        )

        if (isReport) {
            tableRow.unshift({text: item.referrer_name + '(' + item.referrer_serial_no + ')'});
        }

        return tableRow
    }

    const getTotal = (totalValue, isReport, isBillCollection) => {
        let colSpanValue = isReport ? 4 : 3;
        let emptyCells = isReport ? 3 : 2;

        if (isBillCollection && isReport){
            colSpanValue = 4;
            emptyCells =  3
        }

        if (isBillCollection && !isReport){
            colSpanValue = 3;
            emptyCells =  2
        }

          const lastRow = [
                {
                    text: 'Total',
                    alignment: 'right',
                    colSpan: colSpanValue
                },

                 ...Array(emptyCells).fill({}),

                {
                    text: totalValue.total_invoice,
                    alignment: 'right'
                },

                {
                    text: totalValue.total_discount,
                    alignment: 'right'
                },

                {
                    text: totalValue.net_bill,
                    alignment: 'right'
                },
            ]

            if (isBillCollection && (!isReport || isReport)){
                return lastRow
            }

            lastRow.push(
                {
                    text: totalValue.total_incentive,
                    alignment: 'right'
                },

                {
                    text: totalValue.total_referrer_discount,
                    alignment: 'right'
                },

                {
                    text: totalValue.net_incentive,
                    alignment: 'right'
                }
            )

        return lastRow
    }

    const getReferrerIncentiveLabel = (row) => {
        const label = 'Total Incentive Amount';
        return row.referrer_fees_percentage ? `${label} (${row.referrer_fees_percentage}%)` : label;
    }

    const getReferrerIncentiveBox = (group, customizationFields) => {
        if (! isVisible(customizationFields, 'body_totals_table_total_incentive_amount')) return [];

        return [
            {
                margin: [0, 10, 0, 0],
                unbreakable: true,
                columns: [
                    { width: '50%', text: '' },
                    {
                        width: '50%',
                        table: {
                            widths: ['60%', '40%'],
                            body: [
                                [
                                    { text: getReferrerIncentiveLabel(group[0]), bold: true, fillColor: '#f3f2f7' },
                                    { text: commaFormat(sumBy(group, row => Number(row.net_incentive) || 0)), bold: true, alignment: 'right' }
                                ]
                            ]
                        },
                        layout: {
                            hLineWidth: function () {
                                return 0.5;
                            },
                            vLineWidth: function () {
                                return 0.5;
                            }
                        }
                    }
                ]
            }
        ];
    }

    return {
        exportToPDF
    }

}

export default pdfPrinter;